import { useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { FileText, Loader2, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DenialAppealLetter = ({ claim }) => {
  const [letter, setLetter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const generateLetter = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/revq/denial-appeal-letter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          claim_id: claim.id,
          patient_name: claim.patient,
          patient_dob: claim.patientInfo?.dob,
          payer: claim.payer,
          provider: claim.provider,
          npi: claim.npi || claim.providerInfo?.npi,
          dos: claim.dos,
          total: claim.total,
          diagnoses: (claim.dx || []).map(d => ({ code: d.code, desc: d.desc })),
          lines: (claim.lines || []).map(l => ({ cpt: l.cpt, charge: l.charge, units: l.units })),
          denial_reasons: (claim.checks || []).filter(c => !c.pass).map(c => c.label),
          ai_recommendation: claim.aiRec,
        }),
      });
      if (!res.ok) throw new Error('Failed to generate');
      const data = await res.json();
      setLetter(data.letter);
    } catch (e) {
      toast.error('Failed to generate appeal letter');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(letter);
    setCopied(true);
    toast.success('Letter copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  // View draft state (letter already generated)
  if (letter) {
    return (
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }} data-testid="section-appeal-letter">
        <div className="flex items-center gap-3.5" style={{ padding: '14px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText style={{ width: 18, height: 18, color: '#6b5ce7' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="flex items-center gap-2">
              <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Denial appeal letter</span>
              <Badge style={{ fontSize: 10, border: 'none', background: '#f0edff', color: '#5b21b6', borderRadius: 3, padding: '1px 5px' }}>AI Draft</Badge>
            </div>
            <p style={{ fontSize: 11, color: '#666', marginTop: 2 }}>AI drafts a pre-emptive appeal based on risk factors above</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copyToClipboard} className="flex items-center gap-1 transition-colors hover:opacity-80" style={{ fontSize: 10, fontWeight: 500, padding: '4px 10px', borderRadius: 6, background: copied ? '#dcfce7' : '#f0f0f0', color: copied ? '#166534' : '#555', border: 'none', cursor: 'pointer' }} data-testid="copy-appeal-btn">
              {copied ? <CheckCircle2 style={{ width: 10, height: 10 }} /> : <Copy style={{ width: 10, height: 10 }} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
        <div style={{ padding: '14px 16px', maxHeight: 280, overflowY: 'auto', whiteSpace: 'pre-wrap', fontSize: 11, color: '#333', lineHeight: '1.7', fontFamily: 'Georgia, serif' }} data-testid="appeal-letter-content">
          {letter}
        </div>
      </div>
    );
  }

  // Generate state
  return (
    <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, padding: 0 }} data-testid="section-appeal-letter">
      <div className="flex items-center gap-3.5" style={{ padding: '14px 16px' }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FileText style={{ width: 18, height: 18, color: '#6b5ce7' }} />
        </div>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 500, color: '#111' }}>Denial appeal letter</span>
          <p style={{ fontSize: 11, color: '#666', marginTop: 2 }}>AI drafts a pre-emptive appeal based on risk factors above</p>
        </div>
        <button onClick={generateLetter} disabled={loading} className="flex items-center justify-center gap-1.5 transition-opacity hover:opacity-90" style={{ height: 32, padding: '0 14px', borderRadius: 7, background: '#6b5ce7', color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }} data-testid="generate-appeal-btn">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Generate draft'}
        </button>
      </div>
    </div>
  );
};

export default DenialAppealLetter;
